import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RideFormData } from '@/types/ride';
import { useRideCreation } from '@/hooks/ride/useRideCreation';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, MapPin, Radio } from 'lucide-react';

interface RideConfirmationProps {
  rideDetails: RideFormData;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Requested-first booking flow: the ride is created immediately with
 * status='requested' and broadcast to online drivers. When a driver
 * claims it (status -> 'negotiating'), we route straight into the real
 * negotiation screen. Replaces the pre-2026-07 client-side "finding
 * driver" simulation.
 */
const RideConfirmation: React.FC<RideConfirmationProps> = ({
  rideDetails,
  onConfirm,
  onCancel,
}) => {
  const { rideId, createRideRecord, isLoading } = useRideCreation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [cancelling, setCancelling] = useState(false);
  const creatingRef = useRef(false);

  // Create the ride exactly once on mount.
  useEffect(() => {
    if (creatingRef.current) return;
    creatingRef.current = true;
    void createRideRecord(null, rideDetails).then((result) => {
      if (!result.success) onCancel();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch the ride: when a driver claims it, jump into the negotiation.
  useEffect(() => {
    if (!rideId) return;

    const goToNegotiation = async () => {
      const { data } = await supabase
        .from('negotiations')
        .select('id')
        .eq('ride_id', rideId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (data?.id) {
        navigate(`/negotiations/${data.id}`);
      } else {
        // Negotiation row not visible yet (Realtime races the insert) —
        // the rides page picks it up on next status change.
        navigate(`/rides/${rideId}`);
      }
    };

    const channel = supabase
      .channel(`ride-wait-${rideId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'rides', filter: `id=eq.${rideId}` },
        (payload) => {
          const status = (payload.new as { status?: string }).status;
          if (status === 'negotiating') void goToNegotiation();
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [rideId, navigate]);

  const handleCancel = async () => {
    if (!rideId) {
      onCancel();
      return;
    }
    setCancelling(true);
    const { error } = await supabase
      .from('rides')
      .update({ status: 'cancelled', cancellation_reason: 'Cancelled while requesting' })
      .eq('id', rideId);
    setCancelling(false);
    if (error) {
      toast({
        title: 'Could not cancel',
        description: error.message,
        variant: 'destructive',
      });
      return;
    }
    toast({ title: 'Request cancelled' });
    onCancel();
  };

  return (
    <Card>
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center gap-2">
          <Radio className="h-5 w-5 text-primary animate-pulse" />
          {isLoading ? 'Requesting your ride…' : 'Finding you a driver'}
        </CardTitle>
        <CardDescription>
          {isLoading
            ? 'Setting up your request'
            : 'Your request is live. Nearby drivers can now claim it and open a price negotiation.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex justify-center py-6" role="status" aria-live="polite">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <span className="sr-only">Waiting for a driver to accept your request</span>
        </div>

        <Separator />

        <div className="space-y-3 text-sm">
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div>
              <p className="text-muted-foreground">Pickup</p>
              <p className="font-medium">{rideDetails.pickupLocation}</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div>
              <p className="text-muted-foreground">Destination</p>
              <p className="font-medium">
                {rideDetails.destination ?? rideDetails.dropoffLocation}
              </p>
            </div>
          </div>
          {typeof rideDetails.estimatedPrice === 'number' && (
            <div className="flex justify-between pt-1">
              <span className="text-muted-foreground">Market estimate</span>
              <span className="font-medium">${rideDetails.estimatedPrice.toFixed(2)}</span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {rideId && (
            <Button variant="secondary" onClick={onConfirm}>
              Keep waiting in the background
            </Button>
          )}
          <Button variant="outline" onClick={handleCancel} disabled={cancelling}>
            {cancelling ? 'Cancelling…' : 'Cancel request'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default RideConfirmation;
